import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Copy, Trash2, Loader2, Check, FileJson, ChromeIcon,
  ExternalLink, ShieldCheck, AlertCircle, Cpu, KeyRound,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Efiling() {
  const [forms, setForms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [formKey, setFormKey] = useState("MGT-7A");
  const [fy, setFy] = useState("");
  const [directorDin, setDirectorDin] = useState("");
  const [busy, setBusy] = useState("");
  const [newToken, setNewToken] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/mca-efiling/forms`, { withCredentials: true }).then((r) => setForms(r.data)),
      axios.get(`${API}/companies`, { withCredentials: true }).then((r) => setCompanies(Array.isArray(r.data) ? r.data : [])),
      refreshTokens(),
    ]).catch(() => {});
    // Default FY: previous FY
    const y = new Date().getFullYear();
    setFy(`${y - 1}-${String(y).slice(2)}`);
  }, []);

  const refreshTokens = () =>
    axios.get(`${API}/mca-efiling/ext-tokens`, { withCredentials: true })
      .then((r) => setTokens(r.data?.tokens || []))
      .catch(() => {});

  const currentCompany = companies.find((c) => c.id === companyId);
  const currentForm = forms.find((f) => f.key === formKey);
  const needsDirector = currentForm?.scope === "per_director";

  const issueToken = async () => {
    setBusy("token");
    try {
      const r = await axios.post(
        `${API}/mca-efiling/ext-tokens`,
        { label: `Issued ${new Date().toLocaleDateString()}` },
        { withCredentials: true },
      );
      setNewToken(r.data.token);
      await refreshTokens();
      toast.success("Pairing token issued — copy it now, it's shown only once");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue token");
    } finally { setBusy(""); }
  };

  const revokeToken = async (id) => {
    if (!window.confirm("Revoke this token? Any browser using it will be signed out.")) return;
    try {
      await axios.delete(`${API}/mca-efiling/ext-tokens/${id}`, { withCredentials: true });
      await refreshTokens();
      toast.success("Token revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const copyToken = () => {
    if (!newToken) return;
    navigator.clipboard.writeText(newToken);
    toast.success("Token copied to clipboard");
  };

  const downloadExtension = () => {
    window.open(`${API}/mca-efiling/extension/download`, "_blank");
  };

  const downloadJsonDrop = async () => {
    if (!companyId) { toast.error("Pick a company first"); return; }
    if (!currentForm?.supports_json_drop) {
      toast.error(`${formKey} does not support JSON drop — use the extension instead`);
      return;
    }
    setBusy("json");
    try {
      const q = new URLSearchParams();
      if (fy) q.set("fy", fy);
      if (directorDin) q.set("director_din", directorDin);
      const url = `${API}/companies/${companyId}/mca-efiling/${formKey}/json-drop?${q.toString()}`;
      const r = await axios.get(url, { responseType: "blob", withCredentials: true });
      const blob = new Blob([r.data], { type: "application/json" });
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `${formKey}_${(currentCompany?.name || "company").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 30)}_${fy || "FY"}.json`;
      a.click();
      URL.revokeObjectURL(dl);
      toast.success(`${formKey} JSON drop downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate JSON");
    } finally { setBusy(""); }
  };

  // Preview the resolved field-data so the CA sees exactly what will go to MCA
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!companyId || !formKey) { setPreview(null); return; }
    const q = new URLSearchParams({ company_id: companyId, form: formKey });
    if (fy) q.set("fy", fy);
    if (directorDin) q.set("director_din", directorDin);
    axios.get(`${API}/mca-efiling/field-data?${q.toString()}`, { withCredentials: true })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [companyId, formKey, fy, directorDin]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="efiling-page">
      <div className="mb-10">
        <div className="label-eyebrow mb-2">MCA V3 · E-Filing Assist</div>
        <h1 className="font-heading text-4xl font-light tracking-tight mb-2">E-Filing</h1>
        <p className="text-zinc-600 text-sm max-w-2xl leading-relaxed">
          Pre-fill MCA V3 e-forms with your company master data in two ways: a one-click
          <strong> browser extension</strong> that fills the form on mca.gov.in, or download a
          <strong> pre-filled JSON</strong> you upload to MCA&apos;s &ldquo;Upload pre-filled JSON&rdquo; field.
          You stay in control of submission — we never submit forms on your behalf.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mb-10">
        {/* === LEFT — Browser Extension === */}
        <div className="border border-zinc-200 bg-white p-6" data-testid="ext-panel">
          <div className="flex items-center gap-2 mb-3">
            <ChromeIcon size={18} className="text-[#002FA7]" />
            <h2 className="font-heading font-medium text-lg">Browser Extension</h2>
          </div>
          <p className="text-sm text-zinc-600 mb-5 leading-relaxed">
            Runs inside your logged-in MCA portal tab and pre-fills the e-form in one click.
            ToS-compliant — you still review &amp; click Submit yourself.
          </p>

          <div className="space-y-3">
            <button onClick={downloadExtension} className="swiss-btn-primary w-full text-sm" data-testid="download-extension-btn">
              <Download size={14} /> Download Extension (.zip)
            </button>

            <details className="text-xs text-zinc-700 border border-zinc-200 px-3 py-2 bg-zinc-50" data-testid="ext-install-steps">
              <summary className="cursor-pointer font-medium text-zinc-900 py-1">Install instructions (one-time, 60 seconds)</summary>
              <ol className="list-decimal pl-5 space-y-1.5 mt-2 leading-relaxed">
                <li>Download the .zip above and unzip it (right-click → Extract All).</li>
                <li>Open <code className="font-mono-plex bg-white px-1 py-0.5 border border-zinc-200">chrome://extensions</code> (or <code className="font-mono-plex">edge://extensions</code> on Edge).</li>
                <li>Toggle <strong>Developer mode</strong> ON (top-right).</li>
                <li>Click <strong>Load unpacked</strong> and select the unzipped folder.</li>
                <li>Pin the ROC Vault icon to the toolbar.</li>
                <li>Click the icon. In <strong>Server URL</strong>, enter:&nbsp;
                  <code className="font-mono-plex bg-white px-1.5 py-0.5 border border-zinc-200 select-all" data-testid="server-url-hint">
                    {typeof window !== "undefined" ? window.location.origin : "https://rocvault.in"}
                  </code>
                  &nbsp;(👆 this is the host you&apos;re viewing right now).
                </li>
                <li>Paste the <strong>pairing token</strong> from below, click <strong>Pair</strong>.</li>
                <li>Open any MCA e-form (MGT-7A, AOC-4, DIR-3 KYC, INC-22), click the icon, click <strong>Pre-fill</strong>.</li>
              </ol>
              <div className="mt-3 pt-3 border-t border-zinc-200 text-[11px] text-zinc-600">
                <strong>Tip:</strong> if you previously installed an older version of the extension and
                it shows &ldquo;Token rejected&rdquo;, download &amp; reinstall the latest .zip (above),
                then re-pair using the Server URL shown in step 6.
              </div>
            </details>
          </div>

          <div className="mt-6 pt-5 border-t border-zinc-200">
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow flex items-center gap-1.5">
                <KeyRound size={11} /> Pairing tokens
              </div>
              <button
                onClick={issueToken}
                disabled={busy === "token"}
                className="swiss-btn-ghost text-xs"
                data-testid="issue-token-btn"
              >
                {busy === "token" ? <Loader2 size={11} className="animate-spin" /> : null}
                Issue new
              </button>
            </div>

            {newToken && (
              <div className="border border-emerald-300 bg-emerald-50 p-3 mb-3" data-testid="new-token-card">
                <div className="text-[11px] text-emerald-800 mb-1 flex items-center gap-1">
                  <ShieldCheck size={11} /> Token shown only once — copy now
                </div>
                <div className="flex gap-2">
                  <code className="flex-1 font-mono-plex text-[11px] bg-white border border-emerald-200 px-2 py-1.5 break-all">
                    {newToken}
                  </code>
                  <button onClick={copyToken} className="swiss-btn-ghost text-xs" data-testid="copy-token-btn">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
            )}

            {tokens.length === 0 ? (
              <div className="text-xs text-zinc-500 py-3 text-center border border-dashed border-zinc-200 bg-zinc-50">
                No active tokens. Issue one to pair the extension.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="ext-tokens-list">
                {tokens.map((t) => (
                  <div key={t.id} className="flex items-center justify-between border border-zinc-200 px-3 py-2 text-xs">
                    <div className="min-w-0">
                      <div className="font-mono-plex text-[11px] text-zinc-900 truncate">{t.token_preview}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {t.label} · expires {new Date(t.expires_at).toLocaleDateString()}
                        {t.last_used_at && ` · last used ${new Date(t.last_used_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <button
                      onClick={() => revokeToken(t.id)}
                      className="text-zinc-400 hover:text-red-600 shrink-0 ml-2"
                      data-testid={`revoke-token-${t.id}`}
                      aria-label="Revoke"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* === RIGHT — JSON Drop === */}
        <div className="border border-zinc-200 bg-white p-6" data-testid="json-panel">
          <div className="flex items-center gap-2 mb-3">
            <FileJson size={18} className="text-[#002FA7]" />
            <h2 className="font-heading font-medium text-lg">Pre-filled JSON drop</h2>
          </div>
          <p className="text-sm text-zinc-600 mb-5 leading-relaxed">
            Generate the exact JSON shape MCA&apos;s <em>&ldquo;Upload pre-filled JSON&rdquo;</em> field accepts.
            Drop into the form on portal in one click. Works without installing anything.
          </p>

          <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">Company</label>
          <select
            data-testid="json-company-select"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            <option value="">— Select a company —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.cin ? `  ·  ${c.cin}` : ""}</option>
            ))}
          </select>

          <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">MCA Form</label>
          <select
            data-testid="json-form-select"
            value={formKey}
            onChange={(e) => { setFormKey(e.target.value); setDirectorDin(""); }}
            className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          >
            {forms.map((f) => (
              <option key={f.key} value={f.key}>{f.label}{!f.supports_json_drop ? "  (extension only)" : ""}</option>
            ))}
          </select>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">Financial Year</label>
              <select
                data-testid="json-fy-select"
                value={fy}
                onChange={(e) => setFy(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              >
                <option value="">—</option>
                {[0,1,2,3,4].map((i) => {
                  const y = new Date().getFullYear() - i;
                  const v = `${y-1}-${String(y).slice(2)}`;
                  return <option key={v} value={v}>FY {v}</option>;
                })}
              </select>
            </div>
            {needsDirector && currentCompany && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-1.5">Director</label>
                <select
                  data-testid="json-director-select"
                  value={directorDin}
                  onChange={(e) => setDirectorDin(e.target.value)}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                >
                  <option value="">—</option>
                  {(currentCompany.directors || []).filter((d) => d.din).map((d) => (
                    <option key={d.din} value={d.din}>{d.name} · DIN {d.din}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <button
            onClick={downloadJsonDrop}
            disabled={!companyId || busy === "json" || !currentForm?.supports_json_drop}
            className="swiss-btn-primary w-full text-sm disabled:opacity-50 mb-2"
            data-testid="download-json-btn"
          >
            {busy === "json" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Download {formKey} JSON
          </button>
          {currentForm && !currentForm.supports_json_drop && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 mb-3 flex gap-1.5">
              <AlertCircle size={11} className="shrink-0 mt-0.5" />
              MCA does not accept JSON upload for {formKey}. Use the browser extension instead.
            </div>
          )}

          {/* Field preview */}
          {preview && (
            <div className="mt-4 pt-4 border-t border-zinc-200" data-testid="field-preview">
              <div className="label-eyebrow mb-2">What will be filled</div>
              <div className="space-y-1">
                {preview.fields.map((f) => (
                  <div key={f.key} className="grid grid-cols-[1fr_1.5fr] gap-3 text-[11px] py-1 border-b border-zinc-50 last:border-0">
                    <div className="text-zinc-600">{f.labels?.[0] || f.key}{f.required ? <span className="text-red-500"> *</span> : null}</div>
                    <div className={`font-mono-plex ${f.value ? "text-zinc-900" : "text-amber-700"} truncate`}>
                      {f.value || "(blank — fill manually)"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MCA portal direct link */}
      <div className="border border-zinc-200 bg-zinc-50 p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-zinc-700">
          Ready to file? Open MCA V3 portal in a new tab — make sure you&apos;re logged in.
        </div>
        <a
          href="https://www.mca.gov.in/content/mca/global/en/mca/e-filing.html"
          target="_blank"
          rel="noreferrer"
          className="swiss-btn-ghost text-sm"
          data-testid="mca-portal-link"
        >
          <ExternalLink size={14} /> Open MCA Portal
        </a>
      </div>
    </div>
  );
}
